/**
 * Assist worklist rail surface (#378) — the IMS-doctrine ASSIST queue, lifted out
 * of the cramped CommandBar "Queue ▾" dropdown (AssistanceControls) into a proper
 * Coordination sub-tab with an adequate window.
 *
 * Doctrine: an assist request may first arrive as an Urgent Alert, but after it is
 * acknowledged it PERSISTS here as a worklist item with a multi-step lifecycle —
 * **ack ≠ cleared**. Rows are ranked open-first → most-urgent → most-recent (the
 * triage order), carry an urgency accent, and show the 7-state lifecycle stepper
 * (submitted → triaged → routed → assigned → in_progress → fulfilled → closed) so
 * the operator reads where each request sits at a glance. Clicking a row opens the
 * existing AssistanceRequestDetail modal, which drives the forward-only advance /
 * cancel / forward through `PATCH /api/assistance-requests/:id/status` — the row is
 * never deleted, it just changes status and (once terminal) sinks to the bottom.
 *
 * Polled on the shared 30s cadence; also refetches immediately when a request is
 * raised from the CommandBar (the assistBridge nudge). Reports the open
 * (non-terminal) count up for the sub-tab badge (reusing `openAssistCount`).
 */
import { useCallback, useEffect, useState } from 'react';
import type { AssistanceRequest, AssistanceRequestListItem } from '@tsr/shared-types';
import { fetchAssistanceRequest, fetchAssistanceRequests } from './api';
import {
  ASSIST_STATUS_DOT,
  ASSIST_STATUS_LABEL,
  ASSIST_STATUS_ORDER,
  ASSIST_URGENCY_LABEL,
  ASSIST_URGENCY_TONE,
  openAssistCount,
  sortAssistRows,
} from './AssistQueueList';
import { inboundRequestorLabel } from './requestsTabVocabulary';
import { AssistanceRequestDetail } from './AssistanceRequestDetail';
import {
  LifecycleStepper,
  type LifecycleStep,
  type StepperUrgency,
} from './components/LifecycleStepper';
import { useAssistRefresh } from './assistBridge';
import { selectOnCop } from './selectionBridge';
import { showToast } from './lib/toast';

/** Passive re-poll cadence (mirrors the AnomalyQueue / DroneRequests 30s tick). */
export const ASSIST_POLL_MS = 30_000;

/**
 * Pure: build the compact 7-state stepper from a worklist row's status alone (no
 * timestamps — the detail modal shows the stamped version). A `cancelled` request
 * left the linear path, so every step reads `future` and the row renders a separate
 * "Cancelled" note. Order + labels come from the shared AssistQueueList constants so
 * the worklist stepper, the detail-modal stepper, and the queue list stay in lockstep.
 * Reused by the worklist tests.
 */
export function buildAssistListSteps(status: string): LifecycleStep[] {
  const currentIdx = (ASSIST_STATUS_ORDER as readonly string[]).indexOf(status);
  return ASSIST_STATUS_ORDER.map((s, i) => ({
    label: ASSIST_STATUS_LABEL[s] ?? s,
    status:
      currentIdx < 0
        ? 'future'
        : i < currentIdx
          ? 'complete'
          : i === currentIdx
            ? 'current'
            : 'future',
  }));
}

export function AssistQueuePanel({
  onActiveCountChange,
}: {
  /** Report the count of OPEN (non-terminal) requests up for the sub-tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [rows, setRows] = useState<AssistanceRequestListItem[]>([]);
  const [active, setActive] = useState<AssistanceRequest | null>(null);
  // Bumped by the 30s tick AND by the assistBridge nudge → refetch.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setRefreshKey((k) => k + 1), ASSIST_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // A request raised from the CommandBar "New request" button surfaces here at once.
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  useAssistRefresh(bump);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchAssistanceRequests({ limit: 50 }, ctl.signal)
      // sortAssistRows is stable and the server already returns urgency→recency
      // order, so the result is open-first → most-urgent → most-recent.
      .then((r) => setRows(sortAssistRows(r.requests)))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [refreshKey]);

  useEffect(() => {
    onActiveCountChange?.(openAssistCount(rows));
  }, [rows, onActiveCountChange]);

  const openDetail = (id: string): void => {
    void fetchAssistanceRequest(id)
      .then((req) => setActive(req))
      .catch(() => showToast('Could not load that request.', { variant: 'error' }));
  };

  // #426 — fly the COP straight to a row's captured point without opening the detail
  // (reuses the selection bridge's pure point kind). Only shown when the row carries one.
  const locate = (r: AssistanceRequestListItem): void => {
    if (!r.geographic_scope) return;
    selectOnCop({ kind: 'assistance', id: r.id, lat: r.geographic_scope.lat, lon: r.geographic_scope.lon });
  };

  return (
    <div className="px-3 py-3" data-testid="assist-queue-panel">
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        Standing ASSIST worklist (IMS Logistics). A request persists here through its full lifecycle
        after acknowledgement — click one to advance, forward, or cancel it. Updates automatically.
      </p>
      {rows.length === 0 ? (
        <p className="text-[12px] text-slate-500" data-testid="assist-queue-empty">
          No assistance requests.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const cancelled = r.status === 'cancelled';
            return (
              <li key={r.id} data-testid="assist-worklist-row" className="flex items-stretch gap-1">
                <button
                  type="button"
                  data-testid="assist-worklist-open"
                  onClick={() => openDetail(r.id)}
                  className={`min-w-0 flex-1 rounded border-l-2 border-y border-r border-slate-700 bg-slate-800/40 px-3 py-2 text-left hover:bg-slate-800 ${ASSIST_URGENCY_TONE[r.urgency]}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${ASSIST_STATUS_DOT[r.status] ?? 'bg-slate-500'}`}
                      title={ASSIST_STATUS_LABEL[r.status] ?? r.status}
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-100">
                      {inboundRequestorLabel(r.requestor_name)}
                    </span>
                    {/* #419 closed-loop — the PEOC receipt indicator (compact glyph,
                        matching the ↗ forwarded marker; full time in the tooltip). */}
                    {r.acknowledged_at && (
                      <span
                        data-testid="assist-worklist-received"
                        className="shrink-0 text-[10px] text-emerald-400"
                        title={`Received by PEOC · ${r.acknowledged_at}`}
                      >
                        ✓
                      </span>
                    )}
                    {r.forwarded_at && (
                      <span className="shrink-0 text-[10px] text-sky-400" title="Forwarded to PEOC">
                        ↗
                      </span>
                    )}
                    <span
                      className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${ASSIST_URGENCY_TONE[r.urgency]}`}
                    >
                      {ASSIST_URGENCY_LABEL[r.urgency]}
                    </span>
                    {r.response_level != null && (
                      <span className="shrink-0 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-200">
                        L{r.response_level}
                      </span>
                    )}
                  </div>
                  {r.categories.length > 0 && (
                    <p className="mt-0.5 truncate pl-4 text-[11px] text-slate-400">
                      {r.categories.join(' · ')}
                    </p>
                  )}
                  <div className="mt-2">
                    <LifecycleStepper
                      data-testid="assist-worklist-stepper"
                      steps={buildAssistListSteps(r.status)}
                      urgency={r.urgency as StepperUrgency}
                    />
                    {cancelled && (
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-rose-300">
                        Cancelled
                      </p>
                    )}
                  </div>
                </button>
                {r.geographic_scope && (
                  <button
                    type="button"
                    data-testid="assist-worklist-locate"
                    onClick={() => locate(r)}
                    title="Show on map"
                    aria-label={`Show ${r.requestor_name} on map`}
                    className="shrink-0 rounded border border-slate-700 bg-slate-800/40 px-1.5 text-sky-300 hover:bg-slate-800 hover:text-sky-200"
                  >
                    <span aria-hidden>◎</span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {active && (
        <AssistanceRequestDetail
          request={active}
          onClose={() => setActive(null)}
          onUpdated={(req) => {
            setActive(req);
            // The advance/cancel/forward changed the row — refetch the worklist so
            // the status dot + stepper + open count reflect it (ack ≠ cleared: the
            // row stays, it just moves through the lifecycle).
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
